package com.swetdot.swetgram.data.groups

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.toDomain
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.FieldEdit
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.GroupBanEntry
import com.swetdot.swetgram.domain.GroupEdit
import com.swetdot.swetgram.domain.GroupFailure
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.GroupInvite
import com.swetdot.swetgram.domain.GroupInviteAction
import com.swetdot.swetgram.domain.GroupInviteLink
import com.swetdot.swetgram.domain.GroupJoinDecision
import com.swetdot.swetgram.domain.GroupJoinOutcome
import com.swetdot.swetgram.domain.GroupJoinRequestEntry
import com.swetdot.swetgram.domain.GroupJoinRequestId
import com.swetdot.swetgram.domain.GroupMember
import com.swetdot.swetgram.domain.GroupMemberRemoval
import com.swetdot.swetgram.domain.GroupPermissions
import com.swetdot.swetgram.domain.GroupPostingPolicy
import com.swetdot.swetgram.domain.GroupRole
import com.swetdot.swetgram.domain.GroupRoleAssignment
import com.swetdot.swetgram.domain.GroupSettings
import com.swetdot.swetgram.domain.GroupSettingsEdit
import com.swetdot.swetgram.domain.GroupSettingsWrite
import com.swetdot.swetgram.domain.GroupVisibility
import com.swetdot.swetgram.domain.GroupVisibilityChoice
import com.swetdot.swetgram.domain.NewGroup
import com.swetdot.swetgram.domain.Page
import com.swetdot.swetgram.domain.ProfileQr
import com.swetdot.swetgram.domain.QrTarget
import com.swetdot.swetgram.domain.PublicAddress
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.protocol.dto.CreateGroupRequest
import com.swetdot.swetgram.protocol.dto.ErrorCodes
import com.swetdot.swetgram.protocol.dto.GroupBanPage
import com.swetdot.swetgram.protocol.dto.GroupJoinRequestPage
import com.swetdot.swetgram.protocol.dto.GroupJoinRequestStatus
import com.swetdot.swetgram.protocol.dto.GroupJoinStatus
import com.swetdot.swetgram.protocol.dto.GroupMemberPage
import com.swetdot.swetgram.protocol.dto.GroupMemberRoleAssignment
import com.swetdot.swetgram.protocol.dto.GroupPage
import com.swetdot.swetgram.protocol.dto.GroupSettingsUpdate
import com.swetdot.swetgram.protocol.dto.ResolveGroupJoinRequestRequest
import com.swetdot.swetgram.protocol.dto.TransferGroupOwnershipRequest
import com.swetdot.swetgram.protocol.dto.UpdateGroupMemberRoleRequest
import io.ktor.client.request.parameter
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpMethod
import io.ktor.http.content.TextContent
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlin.time.Instant
import com.swetdot.swetgram.protocol.dto.Group as GroupDto
import com.swetdot.swetgram.protocol.dto.GroupBan as GroupBanDto
import com.swetdot.swetgram.protocol.dto.GroupInviteAction as GroupInviteActionDto
import com.swetdot.swetgram.protocol.dto.GroupInvitePreview as GroupInvitePreviewDto
import com.swetdot.swetgram.protocol.dto.GroupJoinDecision as GroupJoinDecisionDto
import com.swetdot.swetgram.protocol.dto.GroupJoinRequest as GroupJoinRequestDto
import com.swetdot.swetgram.protocol.dto.GroupJoinResult as GroupJoinResultDto
import com.swetdot.swetgram.protocol.dto.GroupMember as GroupMemberDto
import com.swetdot.swetgram.protocol.dto.GroupPostingPolicy as GroupPostingPolicyDto
import com.swetdot.swetgram.protocol.dto.GroupRole as GroupRoleDto
import com.swetdot.swetgram.protocol.dto.GroupSettings as GroupSettingsDto
import com.swetdot.swetgram.protocol.dto.QrCode as QrCodeDto
import com.swetdot.swetgram.protocol.dto.GroupVisibility as GroupVisibilityDto
import com.swetdot.swetgram.protocol.dto.InviteLink as InviteLinkDto

/**
 * The Groups data layer: lifecycle, membership, invite links, roles and moderation.
 *
 * | operation                   | verb + path                                     | pass |
 * |-----------------------------|-------------------------------------------------|------|
 * | `listGroups`                | `GET    /groups`                                | 2    |
 * | `createGroup`               | `POST   /groups`                                | 2    |
 * | `getGroup`                  | `GET    /groups/{id}`                           | 2    |
 * | `updateGroup`               | `PATCH  /groups/{id}`                           | 2    |
 * | `deleteGroup`               | `DELETE /groups/{id}`                           | 2    |
 * | `getGroupSettings`          | `GET    /groups/{id}/settings`                  | 2    |
 * | `updateGroupSettings`       | `PATCH  /groups/{id}/settings`                  | 2    |
 * | `transferGroupOwnership`    | `POST   /groups/{id}/ownership`                 | 2    |
 * | `leaveGroup`                | `POST   /groups/{id}/leave`                     | 2    |
 * | `listGroupMembers`          | `GET    /groups/{id}/members`                   | 2    |
 * | `removeGroupMember`         | `DELETE /groups/{id}/members/{user_id}`         | 2    |
 * | `getPublicGroupByUsername`  | `GET    /public-groups/{username}`              | 2    |
 * | `createOrRotateInviteLink`  | `POST   /groups/{id}/invite-link`               | 3    |
 * | `previewGroupInvite`        | `GET    /join/{invite_token}`                   | 3    |
 * | `actOnGroupInvite`          | `POST   /join/{invite_token}`                   | 3    |
 * | `updateGroupMemberRole`     | `PATCH  /groups/{id}/members/{user_id}/role`    | 3    |
 * | `listGroupJoinRequests`     | `GET    /groups/{id}/join-requests`             | 3    |
 * | `resolveGroupJoinRequest`   | `PATCH  /groups/{id}/join-requests/{req_id}`    | 3    |
 * | `listGroupBans`             | `GET    /groups/{id}/bans`                      | 3    |
 * | `banGroupMember`            | `PUT    /groups/{id}/bans/{user_id}`            | 3    |
 * | `unbanGroupMember`          | `DELETE /groups/{id}/bans/{user_id}`            | 3    |
 *
 * THE PASS-3 ROW OF THAT TABLE IS NOW REACHABLE, and the note it replaces is worth keeping in mind
 * because it was RIGHT when it was written. This file was authored against a server whose
 * `GroupRoutes.kt` installed none of these nine operations and whose `Module.kt` constructed neither
 * service, so every path below answered 404 on the VPS -- probed on 2026-08-06 -- and the author
 * said so rather than shipping it quietly. The routes pass then installed all nine (plus
 * `getGroupQr`) and deployed them, and `server/scripts/smoke-groups3.sh` exercises every one of them
 * end to end against the deployed server: 99 assertions, 0 failures.
 *
 * The consequence for the copy, which was the specific worry recorded here: a 404 from any of these
 * paths now genuinely means the group is unavailable to this caller, so [GroupFailure.UNAVAILABLE]
 * and its "this group is no longer available" is the CORRECT sentence rather than a misleading one.
 * It was fixed by installing the routes, which is what the note asked for, not by softening the
 * wording.
 *
 * THERE IS NO ADD-MEMBER CALL BECAUSE THERE IS NO ADD-MEMBER ENDPOINT. v1.0.0 declares none, and
 * the deployed server answers 404 to `POST /groups/{id}/members`, `PUT /groups/{id}/members/{uid}`
 * and `POST /groups/{id}/members/{uid}` alike -- all three were probed. The only ways into an
 * existing group are the invite link ([actOnInvite]) and the join request an admin approves
 * ([resolveJoinRequest]); somebody can also be seeded in at creation through
 * [NewGroup.initialMemberIds]. Inventing a path here would be inventing an endpoint.
 *
 * `getGroupQr` IS NOW SERVED but this client still does not call it, and the reason has changed from
 * "the endpoint does not exist" to a plain scope statement. The routes pass installed it -- a public
 * group answers `payload: https://swetgram.com/g/{group_id}`, a private one answers its current
 * invite link, and an outsider gets a 404 -- so a group QR screen is buildable whenever one is
 * wanted. What is NOT ready is what `QrCode.image_url` advertises: nothing in this deployment serves
 * `/qr/...`, exactly as it does not for `getMyProfileQr`, so a screen that rendered that URL would
 * show a broken image. A client adding this should render its own code from `payload` and ignore
 * `image_url`, which is what the profile QR surface already does.
 *
 * THERE IS NO DISABLE OR REVOKE CALL, and its absence is the contract's. STA §9.2 says "invite
 * token can be disabled"; v1.0.0 has no such operation, so [createOrRotateInviteLink] is the only
 * way to invalidate a link and it always leaves a live replacement. Reported rather than papered
 * over, because a screen offering "Turn off invites" would be offering an endpoint.
 *
 * AUTHENTICATION. [ApiClient.post]'s `authenticated` flag exists because the operations the
 * contract marks `security: []` deadlock if a token is attached -- asking the token provider for
 * one re-enters `SessionManager`'s refresh mutex and the call hangs with no request and no
 * exception. That was checked here and the answer is that it does not apply: `openapi.yaml` sets a
 * global `security: [bearerAuth]` and NO group operation overrides it with `security: []`. The
 * deployed server agrees -- an unauthenticated `GET /groups` is a 401, not a 200. So every call in
 * this file is authenticated, which is the default, and none of them passes the flag.
 *
 * NO LOCAL CACHE, deliberately, and for the reason `ProfileRepository` gives: the chat list is
 * cache-first because it must render offline and instantly, but a group settings form is something
 * the user is looking at and a stale `version` out of SQLite would produce a `409 version_conflict`
 * on save with nothing on screen to explain it.
 *
 * NO CONDITIONAL GET, and this one is a trap worth naming. `listGroups` supports `If-None-Match`
 * and the server really does answer `304` with the ETag echoed -- probed. But [ApiClient] treats
 * anything outside 2xx as a failure, so a 304 would arrive as
 * `ApiError.Server(304, null, "Unexpected response (304)")`: an empty list would look like a server
 * fault. Caching is only worth having with somewhere to cache into, which is pass 3's problem, so
 * this pass never sends the header and never sees the 304.
 */
class GroupRepository(
    private val api: ApiClient,
) {

    // -------------------------------------------------------------------------- lifecycle

    /**
     * `listGroups`, cursor-paginated. The Groups tab (SPS §7.1).
     *
     * A null [cursor] asks for the first page and sends no `cursor` parameter at all; pass
     * [Page.nextCursor] back to continue. The cursor is the server's own opaque encoding -- never
     * parse it, compare it, or keep it across a sign-out.
     */
    suspend fun groups(cursor: String? = null, limit: Int = PAGE_SIZE): Page<Group> {
        val page: GroupPage = api.get("groups") {
            // Ktor drops a null parameter, so a first page carries no cursor at all.
            parameter("cursor", cursor)
            parameter("limit", limit.coerceIn(MIN_LIMIT, MAX_LIMIT))
        }
        return Page(
            items = page.items.map { it.toDomain() },
            nextCursor = page.pageInfo.nextCursor,
            hasMore = page.pageInfo.hasMore,
        )
    }

    /**
     * `createGroup`. 201.
     *
     * NEVER RETRIED, and that is not [ApiClient]'s default being left alone by accident. The
     * contract's idempotency convention is a body `client_id` plus an `Idempotency-Key` header, and
     * `CreateGroupRequest` has no `client_id` -- so there is nothing for the server to collapse a
     * duplicate against, and a replayed POST would leave the user with two identical groups.
     * Passing no key is what keeps `ApiClient` from repeating it.
     *
     * Requires a verified email (`x-requires-verified-email: true`); the server answers
     * `403 email_not_verified`, which maps to [GroupFailure.EMAIL_NOT_VERIFIED]. Creation is also
     * the one group operation the contract puts a `429` on -- DECISIONS §12 caps it at 5 a day.
     *
     * [NewGroup.initialMemberIds] is de-duplicated and length-checked here: the contract says
     * `uniqueItems: true, maxItems: 100`, a `List` cannot express uniqueness, and duplicates would
     * collide on the server's membership primary key rather than earning a clean 400.
     */
    suspend fun create(group: NewGroup): Group {
        val name = group.name.trim()
        require(name.isNotEmpty()) { "A group needs a name" }
        val members = group.initialMemberIds.map { it.value }.distinct()
        require(members.size <= MAX_INITIAL_MEMBERS) {
            "A new group takes at most $MAX_INITIAL_MEMBERS initial members"
        }

        val body = CreateGroupRequest(
            name = name,
            description = group.description?.trim()?.takeIf { it.isNotEmpty() },
            visibility = when (group.visibility) {
                is GroupVisibilityChoice.Public -> GroupVisibilityDto.PUBLIC
                GroupVisibilityChoice.Private -> GroupVisibilityDto.PRIVATE
            },
            // Public-group usernames share the user `Username` schema, which is lowercase-only, so
            // normalising here turns a 400 the user cannot act on into the request they meant.
            username = (group.visibility as? GroupVisibilityChoice.Public)
                ?.username?.trim()?.lowercase(),
            avatarMediaId = group.avatarMediaId,
            initialMemberIds = members,
        )
        return api.post<GroupDto>("groups") { setBody(body) }.toDomain()
    }

    /** `getGroup`. A group that does not exist and one the caller may not see are one 404. */
    suspend fun group(id: GroupId): Group = api.get<GroupDto>("groups/${id.value}").toDomain()

    /**
     * `getPublicGroupByUsername`. How a `/g/{username}` link and the Discover surface resolve.
     *
     * A separate collection from `/users/{username}`: public-group usernames are their own
     * namespace, so `@alex` the group and `@alex` the person can both exist and are two different
     * lookups (DECISIONS §7). Lowercased for the same reason as everywhere else.
     *
     * The returned [Group.role] still describes the CALLER, who may well not be a member -- so read
     * [Group.permissions] before offering any control on this screen.
     */
    suspend fun publicGroup(username: String): Group =
        api.get<GroupDto>("public-groups/${username.trim().lowercase()}").toDomain()

    /** Join a public group directly from its preview, without manufacturing an invite token. */
    suspend fun joinPublic(id: GroupId): Group {
        val result = api.post<GroupJoinResultDto>("groups/${id.value}/join").toDomain()
        return when (result) {
            is GroupJoinOutcome.Joined -> result.group ?: group(result.groupId)
            is GroupJoinOutcome.Requested -> error("A public group cannot create a join request")
        }
    }

    /**
     * `updateGroup`. Name, description, username and avatar; never visibility.
     *
     * The body is hand-built as JSON rather than encoded from `UpdateGroupRequest`, for the reason
     * `ProfileRepository.updateProfile` documents: the contract needs three states per nullable
     * field -- absent, explicit null, value -- and `ProtocolJson` has `explicitNulls = false`, which
     * erases the middle one. `UpdateGroupRequest(description = null)` serializes to `{}` and clears
     * nothing, so a user who deleted a description would watch it come back with a 200 to explain
     * it. `TextContent` is an `OutgoingContent`, which content negotiation passes through untouched,
     * so nothing gets a second opinion about this body.
     *
     * Throws [IllegalArgumentException] before any request when the edit is empty: `minProperties:
     * 1` makes that a guaranteed `400 validation_failed` on `body`.
     */
    suspend fun update(id: GroupId, edit: GroupEdit): Group {
        require(edit.hasChanges) { "A group edit must change at least one field" }
        val body = TextContent(edit.toJsonBody().toString(), ContentType.Application.Json)
        return api.patch<GroupDto>("groups/${id.value}") { setBody(body) }.toDomain()
    }

    /**
     * `deleteGroup`. 204, owner-only, permanent for everybody in the group.
     *
     * It takes the conversation with it -- messages, membership, pins, the lot -- so this is the
     * one group action that needs a confirmation the user cannot mistake for leaving. It is also
     * the only way out for a sole owner, since [leave] answers `ownership_transfer_required` with
     * nobody to transfer to.
     */
    suspend fun delete(id: GroupId) {
        api.delete<Unit>("groups/${id.value}")
    }

    // --------------------------------------------------------------------------- settings

    /** `getGroupSettings`. Read this immediately before opening the form; it carries the version. */
    suspend fun settings(id: GroupId): GroupSettings =
        api.get<GroupSettingsDto>("groups/${id.value}/settings").toDomain()

    /**
     * `updateGroupSettings`. Owner-only, and the ONE place a group can answer
     * `409 version_conflict`.
     *
     * Returns [GroupSettingsWrite] instead of throwing on the conflict. A stale version is not a
     * failure a screen can report and walk away from: the form is describing a revision that no
     * longer exists, and the only way forward is to re-read and let the user re-apply their choice.
     * So the re-read happens here, once, and arrives as [GroupSettingsWrite.Stale] -- an exhaustive
     * `when` then makes it impossible for a caller to treat a lost race as a save. If the re-read
     * ITSELF fails, that error propagates as its own [ApiError] rather than being swallowed into a
     * "conflict" the client cannot substantiate.
     *
     * Unlike [update] this body IS encoded from the DTO, and the difference is not an inconsistency.
     * `GroupSettingsUpdate`'s policies are optional-meaning-unchanged and have no "clear" state, so
     * `explicitNulls = false` dropping a null is exactly the semantics the contract asks for. Only
     * the fields [GroupSettingsEdit] actually mentions go on the wire, which is also what stops the
     * boundary's fail-closed mapping of an unreadable policy from being written back as a choice.
     *
     * Throws [IllegalArgumentException] on an edit with no policy in it: `minProperties: 2` makes a
     * version-only body a guaranteed 400.
     */
    suspend fun updateSettings(id: GroupId, edit: GroupSettingsEdit): GroupSettingsWrite {
        require(edit.hasChanges) { "A settings edit must change at least one policy" }
        val body = GroupSettingsUpdate(
            discoverPosting = edit.discoverPosting?.toDto(),
            chatPosting = edit.chatPosting?.toDto(),
            version = edit.version,
        )
        return try {
            val updated = api.patch<GroupSettingsDto>("groups/${id.value}/settings") { setBody(body) }
            GroupSettingsWrite.Updated(updated.toDomain())
        } catch (conflict: ApiError.VersionConflict) {
            // Nothing was written. Hand back what the server holds now so the form can be rebuilt
            // in one step; the caller does not have to know that a conflict implies a refetch.
            GroupSettingsWrite.Stale(settings(id))
        }
    }

    // -------------------------------------------------------------------------- membership

    /**
     * `listGroupMembers`, cursor-paginated. The Members tab of SPS §12.6.
     *
     * Search is not a parameter the contract offers on this operation, so member search filters the
     * loaded pages rather than asking the server.
     */
    suspend fun members(id: GroupId, cursor: String? = null, limit: Int = PAGE_SIZE): Page<GroupMember> {
        val page: GroupMemberPage = api.get("groups/${id.value}/members") {
            parameter("cursor", cursor)
            parameter("limit", limit.coerceIn(MIN_LIMIT, MAX_LIMIT))
        }
        return Page(
            items = page.items.map { it.toDomain() },
            nextCursor = page.pageInfo.nextCursor,
            hasMore = page.pageInfo.hasMore,
        )
    }

    /**
     * `removeGroupMember`. 204.
     *
     * `action` is a REQUIRED QUERY parameter, not a body -- omitting it is
     * `400 validation_failed` on `action`, confirmed against the deployed server -- which is why
     * `protocol/dto/Groups.kt` deliberately models no `@Serializable` type for it. It is sent as
     * the contract's own spelling here.
     *
     * [GroupMemberRemoval.REMOVE_AND_BAN] is reachable but unused in this pass; the ban list and
     * the dialog that would offer it are pass 3.
     *
     * REMOVING YOURSELF IS LEAVING. The server routes both to the same write, so an owner who
     * passes their own id gets `409 ownership_transfer_required` rather than a permission answer.
     * Call [leave] for that rather than relying on the alias.
     */
    suspend fun removeMember(
        id: GroupId,
        userId: UserId,
        action: GroupMemberRemoval = GroupMemberRemoval.REMOVE,
    ) {
        api.delete<Unit>("groups/${id.value}/members/${userId.value}") {
            parameter(
                "action",
                when (action) {
                    GroupMemberRemoval.REMOVE -> "remove"
                    GroupMemberRemoval.REMOVE_AND_BAN -> "remove_and_ban"
                },
            )
        }
    }

    /**
     * `leaveGroup`. 204.
     *
     * Answers `409 ownership_transfer_required` for the owner, whatever the member count -- so a
     * sole owner's only exit is [delete]. [GroupPermissions.canLeave] is false for an owner, which
     * should keep the action off screen; the 409 still has to be handled, because ownership can be
     * transferred TO someone while they have this sheet open. Map it with [asGroupFailure].
     *
     * Leaving a group you are not in is a 204, not an error, so a double tap is harmless.
     */
    suspend fun leave(id: GroupId) {
        api.post<Unit>("groups/${id.value}/leave")
    }

    /**
     * `transferGroupOwnership`. 204. Owner-only.
     *
     * The current owner becomes an Admin, which is what then makes [leave] possible for them. The
     * target must be another live member of the group: the server answers
     * `400 validation_failed` on `new_owner_user_id` both for a non-member and for the caller
     * themselves, and naming the bad target leaks nothing, since the owner can list the members
     * anyway.
     *
     * Included in this pass even though role changes belong to pass 3, because it is the only thing
     * that resolves `ownership_transfer_required` -- an owner who cannot transfer can only delete.
     * `updateGroupMemberRole`, the promote/demote operation, is genuinely pass 3 and 404s today.
     */
    suspend fun transferOwnership(id: GroupId, newOwner: UserId) {
        api.post<Unit>("groups/${id.value}/ownership") {
            setBody(TransferGroupOwnershipRequest(newOwnerUserId = newOwner.value))
        }
    }

    /**
     * `updateGroupMemberRole`. 204. Owner only, both directions.
     *
     * Promotion and demotion are ONE operation with two body values, which is why the domain offers
     * `Group.nextRoleFor` rather than two methods. [GroupRoleAssignment] cannot express `owner`, so
     * this call can never move ownership -- [transferOwnership] is the only thing that does.
     *
     * Answers `403 insufficient_group_role` for an admin who tries, and
     * `409 ownership_transfer_required` for the owner as a target (including themselves): a group
     * has exactly one owner, so "demote the owner" and "demote the last owner" are the same request
     * and the server refuses both the same way.
     */
    suspend fun updateMemberRole(id: GroupId, userId: UserId, role: GroupRoleAssignment) {
        api.patch<Unit>("groups/${id.value}/members/${userId.value}/role") {
            setBody(UpdateGroupMemberRoleRequest(role = role.toDto()))
        }
    }

    // -------------------------------------------------------------------------- invite links

    /**
     * `createOrRotateInviteLink`. 200 [GroupInviteLink].
     *
     * ONE OPERATION, TWO ACTS, AND IT MUTATES. There is no `GET /groups/{id}/invite-link` in
     * v1.0.0, so this is the only way to see a link and calling it is a write. For an Owner or an
     * Admin it ROTATES: whatever the group had already shared stops working, immediately and
     * irreversibly, because the schema allows exactly one active link per group and no operation
     * restores an old one. For a public group's plain member it returns the CURRENT link without
     * writing (DECISIONS §7 -- "all members may share the invitation"), and mints the first one if
     * the group has none; a private group's plain member is refused `403 insufficient_group_role`.
     *
     * THE CONSEQUENCE FOR CALLERS IS THAT A SCREEN MAY NOT CALL THIS ON ITS OWN INITIATIVE. Opening
     * a screen must never rotate a link, so this is only ever reached from an explicit tap whose
     * copy says what the tap will do. See `GroupInviteViewModel`, which is built around that rule.
     *
     * NEVER RETRIED, and here that matters more than anywhere else in this file: [ApiClient] retries
     * a POST only when given an idempotency key, none is passed, and a replayed rotation would mint
     * a SECOND token and invalidate the one the first attempt had already handed the user. A
     * timeout on this call therefore leaves the client genuinely unable to say which link is live,
     * which is a state the screen has to handle rather than hide.
     *
     * The returned [GroupInviteLink.token] is a credential. Do not log it, do not put it in an
     * analytics event, and do not build the URL from it -- [GroupInviteLink.url] is the server's.
     */
    suspend fun createOrRotateInviteLink(id: GroupId): GroupInviteLink =
        api.post<InviteLinkDto>("groups/${id.value}/invite-link").toDomain()

    /** Immutable public-group QR, or the current private invite credential for authorised roles. */
    suspend fun qr(id: GroupId): ProfileQr =
        api.get<QrCodeDto>("groups/${id.value}/qr").let { dto ->
            ProfileQr(
                payload = dto.payload,
                imageUrl = dto.imageUrl,
                target = when (dto.targetType) {
                    com.swetdot.swetgram.protocol.dto.QrTargetType.PUBLIC_GROUP -> QrTarget.PUBLIC_GROUP
                    com.swetdot.swetgram.protocol.dto.QrTargetType.PRIVATE_GROUP_INVITE ->
                        QrTarget.PRIVATE_GROUP_INVITE
                    com.swetdot.swetgram.protocol.dto.QrTargetType.USER -> QrTarget.USER
                    com.swetdot.swetgram.protocol.dto.QrTargetType.UNKNOWN -> QrTarget.UNKNOWN
                },
                targetId = dto.targetId,
            )
        }

    /**
     * `previewGroupInvite`. What a link resolves to, before anybody joins anything.
     *
     * SAFE TO CALL ON SCREEN ENTRY, unlike everything else on this surface: it is a GET, it writes
     * nothing, and it is the whole point of the preview -- the contract's own summary is "Preview a
     * group before joining or requesting access".
     *
     * ONE 404 FOR EVERY UNUSABLE TOKEN. Unknown, revoked by a rotation, expired, or pointing at a
     * deleted group all answer `404 conversation_not_found`, which arrives as
     * [GroupFailure.UNAVAILABLE]. A screen must NOT try to tell those apart or guess between them:
     * the server refuses to, deliberately, because a distinguishable "this was once real" tells
     * whoever holds a rotated link exactly what a private group's link exists to keep private.
     *
     * A MALFORMED TOKEN NEVER LEAVES THE DEVICE. [isPlausibleInviteToken] rejects it first and this
     * throws [IllegalArgumentException] before any request. Two reasons, and neither is tidiness:
     * the token is a raw path segment, so a value containing `/` or `?` would address some other
     * endpoint entirely; and a token that fails the contract's own `minLength: 20` cannot be one
     * this server minted, so asking would spend a round trip to be told what is already known.
     */
    suspend fun invitePreview(token: String): GroupInvite {
        val safe = requireInviteToken(token)
        return api.get<GroupInvitePreviewDto>("join/$safe").toDomain()
    }

    /**
     * `actOnGroupInvite`. 200 joined, or 202 request created.
     *
     * BRANCHED ON `status`, NOT ON THE HTTP CODE. [ApiClient] hands back the body for any 2xx and
     * does not report which one it was, and the contract sends the same schema for both -- so
     * [GroupJoinOutcome] is built from `GroupJoinResult.status`, which is what the DTO itself says
     * to branch on. Guessing from the presence of `group` would send somebody into a group they are
     * not a member of on any response that omitted it.
     *
     * IDEMPOTENT BY DESIGN. Joining a group you are already in is `200 joined` with the group, not
     * an error -- a link is a thing people tap twice. What is NOT idempotent is asking a private
     * group twice: the second attempt is `409 join_request_pending`, which is
     * [GroupFailure.JOIN_REQUEST_PENDING] and describes a state rather than a failure.
     *
     * The refusals this can answer that the contract does not list on the operation:
     * `403 group_banned` ([GroupFailure.BANNED]) -- which the preview cannot warn about, because
     * its `action` enum has no value for it; `403 email_not_verified`; `409 group_full`; and a 429.
     * Every one of them is in `ERRORS.md`; only the per-operation response list omits them.
     */
    suspend fun actOnInvite(token: String): GroupJoinOutcome {
        val safe = requireInviteToken(token)
        return api.post<GroupJoinResultDto>("join/$safe").toDomain()
    }

    // ---------------------------------------------------------------------------- moderation

    /**
     * `listGroupJoinRequests`, cursor-paginated. A private group's approval queue.
     *
     * Newest first -- the server pages an index declared `(created_at DESC, id DESC)` -- and the
     * contract sets no order, so a screen must not re-sort or it will interleave pages wrongly.
     *
     * A PUBLIC GROUP ANSWERS AN EMPTY PAGE rather than an error: a public group joins immediately,
     * so "no pending requests" is simply true of it. The client still hides the whole surface for a
     * public group (`Group.hasJoinQueue`), because a screen that can only ever be empty is not a
     * screen worth navigating to.
     */
    suspend fun joinRequests(
        id: GroupId,
        cursor: String? = null,
        limit: Int = PAGE_SIZE,
    ): Page<GroupJoinRequestEntry> {
        val page: GroupJoinRequestPage = api.get("groups/${id.value}/join-requests") {
            parameter("cursor", cursor)
            parameter("limit", limit.coerceIn(MIN_LIMIT, MAX_LIMIT))
        }
        return Page(
            items = page.items.map { it.toDomain() },
            nextCursor = page.pageInfo.nextCursor,
            hasMore = page.pageInfo.hasMore,
        )
    }

    /**
     * `resolveGroupJoinRequest`. 204.
     *
     * 204 FOR A REQUEST THAT IS NO LONGER PENDING, which is the case that actually happens: two
     * admins tap Approve on the same row, or one admin retries after a timeout. The operation
     * declares no status for absence, so the server answers success and the client must treat a
     * second resolution as done rather than as a failure. The honest cost is that a garbage request
     * id also succeeds silently -- there is no way to tell the two apart from out here.
     *
     * AN APPROVAL CAN STILL FAIL, and for reasons that are about the group rather than the request:
     * `409 group_full` when the group filled up while the request sat in the queue (the cap is
     * enforced on approval, not when the request is filed), and `403 group_banned` when the
     * applicant was banned in the meantime. Both are worth different words on screen than "could
     * not approve".
     */
    suspend fun resolveJoinRequest(
        id: GroupId,
        requestId: GroupJoinRequestId,
        decision: GroupJoinDecision,
    ) {
        api.patch<Unit>("groups/${id.value}/join-requests/${requestId.value}") {
            setBody(ResolveGroupJoinRequestRequest(decision = decision.toDto()))
        }
    }

    /**
     * `listGroupBans`, cursor-paginated. Owner and Admin.
     *
     * Ordered by user id ascending, because the server pages off the primary key. Not a display
     * order anybody would choose, and not one the contract sets -- stated here so a screen does not
     * quietly re-sort a page and break the cursor's meaning.
     *
     * This is the ONLY read side of a ban. A banned person is not a member, so they are on no
     * member list; without this page a ban is invisible and [unbanMember] has nothing to target.
     */
    suspend fun bans(id: GroupId, cursor: String? = null, limit: Int = PAGE_SIZE): Page<GroupBanEntry> {
        val page: GroupBanPage = api.get("groups/${id.value}/bans") {
            parameter("cursor", cursor)
            parameter("limit", limit.coerceIn(MIN_LIMIT, MAX_LIMIT))
        }
        return Page(
            items = page.items.map { it.toDomain() },
            nextCursor = page.pageInfo.nextCursor,
            hasMore = page.pageInfo.hasMore,
        )
    }

    /**
     * `banGroupMember`. `PUT /groups/{id}/bans/{user_id}`, 204.
     *
     * IT REMOVES THEM TOO. DECISIONS §7 pairs the two acts and the server performs both in one
     * transaction, so after this the person is off the member list, `member_count` has dropped, and
     * any pending join request of theirs has been rejected. A confirmation that says only "ban"
     * would be describing half of what the button does.
     *
     * A PUT, which is why this goes through [ApiClient.execute] rather than one of the verb
     * helpers: the client has no `put`, and adding one would be editing a file this change does not
     * own. No idempotency key, so it is never retried -- correct, since the server's answer to a
     * repeat depends on whether the target is still a member.
     *
     * `403 not_conversation_member` ([GroupFailure.NOT_A_MEMBER]) for somebody who has already left
     * or already been removed. The contract's `PUT` shape could in principle pre-emptively ban a
     * non-member -- the tool a private group needs against an applicant who keeps re-asking -- and
     * the server does not support it; recorded as an open gap in DECISIONS §19 rather than worked
     * around here.
     */
    suspend fun banMember(id: GroupId, userId: UserId) {
        api.execute<Unit>(
            method = HttpMethod.Put,
            path = "groups/${id.value}/bans/${userId.value}",
            idempotencyKey = null,
        ) {}
    }

    /**
     * `unbanGroupMember`. 204.
     *
     * IT DOES NOT RE-ADD THEM. No operation in v1.0.0 puts somebody into a group without their
     * asking, so lifting a ban only restores their ability to use a link or file a request -- and
     * when they do rejoin, they start from the current position in the conversation, so nothing
     * from before the ban comes back with them. A confirmation must say that, because "unban" reads
     * like "undo" and it is not one.
     *
     * 204 for somebody who was never banned, the same reading `unblockUser` already has: a second
     * tap is harmless.
     */
    suspend fun unbanMember(id: GroupId, userId: UserId) {
        api.delete<Unit>("groups/${id.value}/bans/${userId.value}")
    }

    private companion object {
        /** DECISIONS §5's standard first page, matching the contract's `Limit` default. */
        const val PAGE_SIZE = 30
        const val MIN_LIMIT = 1
        const val MAX_LIMIT = 100

        /** `CreateGroupRequest.initial_member_ids`: `maxItems: 100`. */
        const val MAX_INITIAL_MEMBERS = 100
    }
}

// -------------------------------------------------------------------------------- failures

/**
 * The group meaning of a thrown [ApiError], so a ViewModel can branch without importing
 * `protocol` (IMPLEMENTATION_NOTES §10).
 *
 * Codes first, status second, which is the order [ApiError.fromCode] already uses: several group
 * codes share a status with something else. `ownership_transfer_required`, `group_full` and
 * `username_taken` are all 409s, and they mean three entirely different things to the user.
 *
 * Anything with no group meaning -- offline, timeout, 5xx, an expired session, an upgrade demand --
 * stays [GroupFailure.OTHER] and keeps whatever handling it already has. Flattening those into this
 * enum would lose the distinction between "the group refused you" and "there is no network".
 */
fun Throwable.asGroupFailure(): GroupFailure = when (this) {
    is ApiError.Conflict -> when (code) {
        ErrorCodes.OWNERSHIP_TRANSFER_REQUIRED -> GroupFailure.OWNERSHIP_TRANSFER_REQUIRED
        ErrorCodes.GROUP_FULL -> GroupFailure.GROUP_FULL
        // The one producer in the whole API, and it means "you have already asked" rather than
        // "something went wrong" -- see GroupFailure.JOIN_REQUEST_PENDING.
        ErrorCodes.JOIN_REQUEST_PENDING -> GroupFailure.JOIN_REQUEST_PENDING
        ErrorCodes.USERNAME_TAKEN -> GroupFailure.USERNAME_TAKEN
        ErrorCodes.USERNAME_CHANGE_TOO_SOON -> GroupFailure.USERNAME_COOLDOWN
        else -> GroupFailure.OTHER
    }
    is ApiError.Forbidden -> when (code) {
        ErrorCodes.INSUFFICIENT_GROUP_ROLE -> GroupFailure.INSUFFICIENT_ROLE
        ErrorCodes.GROUP_BANNED -> GroupFailure.BANNED
        ErrorCodes.EMAIL_NOT_VERIFIED -> GroupFailure.EMAIL_NOT_VERIFIED
        // The GROUP is visible and the TARGET is not in it: `banGroupMember` against somebody who
        // already left. Kept apart from UNAVAILABLE, which is about the group.
        ErrorCodes.NOT_CONVERSATION_MEMBER -> GroupFailure.NOT_A_MEMBER
        else -> GroupFailure.OTHER
    }
    // `conversation_not_found` covers both "no such group" and "you cannot see it", on purpose:
    // a separate code would turn any held group id into a live existence check.
    is ApiError.NotFound -> GroupFailure.UNAVAILABLE
    is ApiError.RateLimited -> GroupFailure.RATE_LIMITED
    is ApiError.Validation -> GroupFailure.INVALID
    else -> GroupFailure.OTHER
}

// --------------------------------------------------------------------------------- mapping

/*
 * The wire-to-domain boundary for this package. Every enum narrowing below drops the wire's
 * `UNKNOWN` member, and every one of them drops it toward the LEAST permission -- see the notes on
 * `domain/Group.kt`, which is where the reasoning belongs since it is what the screens read.
 *
 * WHAT THESE NARROWINGS DO AND DO NOT ABSORB. `unknown` is a spelled `@SerialName` on every wire
 * enum in `protocol/dto/Groups.kt`, so it decodes and lands in the `else` branches below. A value
 * that is NEITHER a known member NOR that literal does not reach here at all: pass 1 gave these
 * properties no Kotlin default, so `coerceInputValues` has nothing to substitute and the decode
 * fails. That is deliberate and it is the right failure -- what each of these enums decides is a
 * permission or an irreversible action, and reporting that the response was not understood beats
 * rendering a control nobody granted. It surfaces as a thrown `SerializationException`, which
 * `asGroupFailure` reports as [GroupFailure.OTHER] rather than as a refusal by the group.
 */

/** Forgiving on purpose: an unreadable date degrades one field rather than failing a screen. */
private fun parseInstantOrNull(raw: String?): Instant? =
    raw?.let { runCatching { Instant.parse(it) }.getOrNull() }

private fun GroupVisibilityDto.toDomain(): GroupVisibility = when (this) {
    GroupVisibilityDto.PUBLIC -> GroupVisibility.PUBLIC
    // PRIVATE, and UNKNOWN with it. Announcing a group as public on a value this build could not
    // read would advertise a member list and a Discover area that may have been meant for nobody.
    else -> GroupVisibility.PRIVATE
}

private fun GroupRoleDto.toDomain(): GroupRole = when (this) {
    GroupRoleDto.OWNER -> GroupRole.OWNER
    GroupRoleDto.ADMIN -> GroupRole.ADMIN
    // MEMBER, and UNKNOWN with it -- the floor, not a fourth state. Every operation that returns a
    // role has already established that the caller is in the group, so "in it with the fewest
    // permissions" is the honest reading of a role this build cannot name.
    else -> GroupRole.MEMBER
}

private fun GroupPostingPolicyDto.toDomain(): GroupPostingPolicy = when (this) {
    GroupPostingPolicyDto.ALL_MEMBERS -> GroupPostingPolicy.ALL_MEMBERS
    // ADMINS_ONLY, and UNKNOWN with it: the restrictive member. A policy the client misread must
    // not open a composer the server will then answer `group_chat_read_only` to.
    else -> GroupPostingPolicy.ADMINS_ONLY
}

private fun GroupPostingPolicy.toDto(): GroupPostingPolicyDto = when (this) {
    GroupPostingPolicy.ALL_MEMBERS -> GroupPostingPolicyDto.ALL_MEMBERS
    GroupPostingPolicy.ADMINS_ONLY -> GroupPostingPolicyDto.ADMINS_ONLY
}

internal fun GroupSettingsDto.toDomain(): GroupSettings = GroupSettings(
    discoverPosting = discoverPosting.toDomain(),
    chatPosting = chatPosting.toDomain(),
    invitePermission = invitePermission.toDomain(),
    version = version,
)

internal fun GroupDto.toDomain(): Group = Group(
    id = GroupId(id),
    // The chat, and a DIFFERENT uuid from `id`. Typing it as ChatId is what stops a caller from
    // handing the group id to a conversation endpoint on the assumption that they match.
    conversationId = ChatId(conversationId),
    name = name,
    description = description,
    avatarUrl = avatarUrl,
    visibility = visibility.toDomain(),
    // Both halves or neither. The contract sends them together for a public group; a lone username
    // would otherwise become a share button that copies a URL the client invented.
    publicAddress = username?.let { handle -> publicUrl?.let { url -> PublicAddress(handle, url) } },
    role = role.toDomain(),
    isMember = isMember,
    memberCount = memberCount,
    settings = settings.toDomain(),
    // Null rather than the epoch on an unreadable date: "created in 1970" is a worse answer on a
    // group information screen than showing nothing at all.
    createdAt = parseInstantOrNull(createdAt),
)

internal fun GroupMemberDto.toDomain(): GroupMember = GroupMember(
    // The shared UserPublic mapping, so a name and an avatar render here exactly as in a chat header.
    user = user.toDomain(),
    role = role.toDomain(),
    // Kept rather than dropped when unparseable: this list is the only place a member can be
    // removed from, so a row has to survive a date the client cannot read.
    joinedAt = parseInstantOrNull(joinedAt),
)

// ------------------------------------------------------------------------------ invite tokens

/**
 * Whether [raw] could be an invite token this server issued, decided without asking it.
 *
 * TWO JOBS, and the first is the reason this is not merely a nicety. The token is spliced into a
 * URL as a raw PATH SEGMENT, so a value containing `/`, `?`, `#` or a space would not address
 * `/join/{token}` at all -- it would address some other endpoint, with the rest of the string as a
 * query. Refusing those here means no screen can turn a pasted string into a request for a resource
 * nobody named.
 *
 * The second job is cheaper: the contract's own `minLength: 20` makes anything shorter a token this
 * server cannot have minted (it issues 43 characters of base64url), so asking about it would spend
 * a round trip to be told what is already known.
 *
 * PERMISSIVE ON PURPOSE ABOUT THE ALPHABET. The set below is RFC 3986's unreserved characters,
 * which is a superset of base64url, rather than base64url exactly -- a client that hard-coded the
 * narrower set would start rejecting valid links the day the server changed its encoding, and
 * "your invite link is broken" is a much worse failure than one wasted request.
 *
 * A refusal here is deliberately INDISTINGUISHABLE on screen from the server's 404: both are "this
 * link does not work". Telling the user that their link was the wrong LENGTH would publish how long
 * a real one is.
 */
internal fun isPlausibleInviteToken(raw: String): Boolean {
    if (raw.length < MIN_INVITE_TOKEN_LENGTH) return false
    return raw.all { c ->
        // ASCII only. `Char.isLetterOrDigit` is Unicode-aware, and a Cyrillic letter is a letter to
        // it -- which would pass a string that is percent-encoded by the HTTP client into something
        // other than what was pasted.
        val ascii = c.code < 128 && (c in 'a'..'z' || c in 'A'..'Z' || c in '0'..'9')
        ascii || c == '-' || c == '_' || c == '.' || c == '~'
    }
}

/** The contract's `minLength: 20` on `/join/{invite_token}`. */
private const val MIN_INVITE_TOKEN_LENGTH = 20

private fun requireInviteToken(raw: String): String {
    val trimmed = raw.trim()
    require(isPlausibleInviteToken(trimmed)) { "Not a usable invite token" }
    return trimmed
}

// ------------------------------------------------------------------------- pass-3 enum mapping

private fun GroupRoleAssignment.toDto(): GroupMemberRoleAssignment = when (this) {
    GroupRoleAssignment.ADMIN -> GroupMemberRoleAssignment.ADMIN
    GroupRoleAssignment.MEMBER -> GroupMemberRoleAssignment.MEMBER
}

private fun GroupJoinDecision.toDto(): GroupJoinDecisionDto = when (this) {
    GroupJoinDecision.APPROVE -> GroupJoinDecisionDto.APPROVE
    GroupJoinDecision.REJECT -> GroupJoinDecisionDto.REJECT
}

/**
 * Null for `unknown`, which is the fail-closed direction here as everywhere else in this file.
 *
 * The other enums resolve an unreadable value to their least-permissive member. This one has no
 * such member -- every value of `GroupInviteAction` offers the user a button -- so the honest
 * mapping is the absence of an action, and the preview screen then shows the group with nothing to
 * press. An unreadable action must not become "join": that is a membership nobody asked for.
 */
private fun GroupInviteActionDto.toDomain(): GroupInviteAction? = when (this) {
    GroupInviteActionDto.JOIN -> GroupInviteAction.JOIN
    GroupInviteActionDto.REQUEST_ACCESS -> GroupInviteAction.REQUEST_ACCESS
    GroupInviteActionDto.ALREADY_MEMBER -> GroupInviteAction.ALREADY_MEMBER
    GroupInviteActionDto.REQUEST_PENDING -> GroupInviteAction.REQUEST_PENDING
    GroupInviteActionDto.UNKNOWN -> null
}

internal fun InviteLinkDto.toDomain(): GroupInviteLink = GroupInviteLink(
    url = url,
    token = token,
    // Null rather than the epoch, like every other date here. A link is worth showing whether or
    // not the client can render when it was made -- it is the only copy of the token.
    createdAt = parseInstantOrNull(createdAt),
)

internal fun GroupInvitePreviewDto.toDomain(): GroupInvite = GroupInvite(
    groupId = GroupId(groupId),
    name = name,
    description = description,
    avatarUrl = avatarUrl,
    visibility = visibility.toDomain(),
    memberCount = memberCount,
    action = action.toDomain(),
)

/**
 * `status` decides, not the presence of `group` and not the HTTP code.
 *
 * `GroupJoinStatus.UNKNOWN` is folded in with `pending_approval` rather than with `joined`, and
 * that is the whole reason this is a `when` and not an `if`. Both non-JOINED branches say "you are
 * not in this group", which is the safe reading: treating an unreadable status as a join would send
 * somebody into a conversation the server never admitted them to, and every read they then made
 * would 404 or 403 with nothing on screen to explain it.
 */
internal fun GroupJoinResultDto.toDomain(): GroupJoinOutcome = when (status) {
    GroupJoinStatus.JOINED -> GroupJoinOutcome.Joined(
        groupId = GroupId(groupId),
        group = group?.toDomain(),
    )
    else -> GroupJoinOutcome.Requested(GroupId(groupId))
}

internal fun GroupJoinRequestDto.toDomain(): GroupJoinRequestEntry = GroupJoinRequestEntry(
    id = GroupJoinRequestId(id),
    groupId = GroupId(groupId),
    user = user.toDomain(),
    // Only `pending` is actionable. `approved`, `rejected` and a status this build could not read
    // all collapse to false, so a resolved row can never be offered Approve and Reject again.
    isPending = status == GroupJoinRequestStatus.PENDING,
    createdAt = parseInstantOrNull(createdAt),
)

internal fun GroupBanDto.toDomain(): GroupBanEntry = GroupBanEntry(
    user = user.toDomain(),
    bannedBy = bannedBy.toDomain(),
    // Kept rather than dropped when unparseable: this page is the only place a ban can be lifted.
    createdAt = parseInstantOrNull(createdAt),
)

/**
 * Builds the `updateGroup` body.
 *
 * Only mentioned fields appear. [FieldEdit.Clear] becomes a literal JSON null, which is the
 * contract's "remove this"; a blank value is treated as a clear, because "" and "no description"
 * are the same thing to a user and storing the empty string would make every reader test for it.
 *
 * `username` has no clear branch. The contract allows a null, but the deployed server answers
 * `400 validation_failed` -- "A public group must keep a username" -- and a private group has none
 * to remove, so the state is unreachable through any screen. `visibility` is absent from the whole
 * body by contract: it is immutable once a group exists.
 */
private fun GroupEdit.toJsonBody(): JsonObject = buildJsonObject {
    name?.let { put("name", JsonPrimitive(it.trim())) }
    // Lowercase-only, like every other Username in the contract.
    username?.let { put("username", JsonPrimitive(it.trim().lowercase())) }

    when (val edit = description) {
        is FieldEdit.Unchanged -> Unit
        is FieldEdit.Clear -> put("description", JsonNull)
        is FieldEdit.Set -> {
            val text = edit.value.trim()
            put("description", if (text.isEmpty()) JsonNull else JsonPrimitive(text))
        }
    }

    when (val edit = avatarMediaId) {
        is FieldEdit.Unchanged -> Unit
        is FieldEdit.Clear -> put("avatar_media_id", JsonNull)
        is FieldEdit.Set -> put("avatar_media_id", JsonPrimitive(edit.value))
    }
}
